import { useEffect, useState } from "react";
import axios from "axios";
import {
  ClipboardList, Wrench, AlertTriangle, CheckCircle,
  Clock, Brain, LoaderPinwheel, FileDown, InboxIcon,
} from "lucide-react";
import { toast } from "react-toastify";
import { descargarReporteIndividual } from "../../../utils/reports";

const getIcon = (tipo) => {
  switch (tipo) {
    case "Preventivo":
      return <Wrench className="h-4 w-4 text-blue-500" />;
    case "Correctivo":
      return <AlertTriangle className="h-4 w-4 text-amber-500" />;
    case "Calibración":
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    default:
      return <Clock className="h-4 w-4 text-gray-400" />;
  }
};

const TIPO_DOT = {
  Preventivo: "bg-blue-500",
  Correctivo: "bg-amber-500",
  "Calibración": "bg-green-500",
};

const Historico = ({
  equipo,
  publicView = false,
  initialGestiones = [],
  initialPredictivos = [],
}) => {
  const [gestiones, setGestiones] = useState(initialGestiones || []);
  const [predictivos, setPredictivos] = useState(initialPredictivos || []);
  const [loading, setLoading] = useState(true);
  const API = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");

  useEffect(() => {
    if (publicView) {
      setGestiones(initialGestiones || []);
      setPredictivos(initialPredictivos || []);
      setLoading(false);
      return;
    }

    const fetchHistorico = async () => {
      try {
        setLoading(true);
        const [resGestiones, resPredictivo] = await Promise.all([
          axios.get(`${API}/gestion/equipo/${equipo._id}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(
            `${API}/predictivo/predicciones?equipoId=${equipo._id}`,
            { headers: { Authorization: `Bearer ${token}` } },
          ),
        ]);
        setGestiones(resGestiones.data || []);
        setPredictivos(resPredictivo.data || []);
      } catch (err) {
        toast.error(err);
      } finally {
        setLoading(false);
      }
    };
    if (equipo?._id) fetchHistorico();
  }, [equipo, API, token, publicView, initialGestiones, initialPredictivos]);

  return (
    <div className="space-y-8">

      {/* ── Mantenimientos ── */}
      <section className="space-y-4">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50">
            <ClipboardList className="h-4 w-4 text-blue-600" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-800">
              Histórico de mantenimientos
            </h3>
            <p className="text-xs text-gray-400">
              {gestiones.length} registro{gestiones.length !== 1 ? "s" : ""}
            </p>
          </div>
        </div>

        {loading && (
          <div className="inline-flex items-center gap-2 rounded-xl border border-blue-100 bg-blue-50 px-4 py-2.5 text-sm text-blue-700">
            <LoaderPinwheel className="h-4 w-4 animate-spin text-blue-600" />
            Cargando datos…
          </div>
        )}

        {!loading && gestiones.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-200 py-10 text-center">
            <InboxIcon className="h-8 w-8 text-gray-200 mx-auto mb-2" />
            <p className="text-sm text-gray-400">Sin registros de mantenimiento</p>
          </div>
        ) : (
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-[18px] top-2 bottom-2 w-0.5 bg-gradient-to-b from-blue-200 to-blue-400 rounded-full" />

            <div className="flex flex-col gap-4 pl-10">
              {gestiones.map((g) => (
                <div
                  key={g._id}
                  className="relative bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-md transition-shadow"
                >
                  {/* Timeline dot */}
                  <div
                    className={`absolute -left-[29px] top-5 w-3.5 h-3.5 rounded-full border-2 border-white shadow-sm ${
                      TIPO_DOT[g.tipo] || "bg-gray-400"
                    }`}
                  />

                  <div className="p-5 space-y-3">
                    {/* Top row */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-50">
                          {getIcon(g.tipo)}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-800">{g.tipo}</p>
                          <p className="text-xs text-gray-400">
                            {new Date(g.createdAt).toLocaleDateString("es-CO")}
                          </p>
                        </div>
                      </div>
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold shrink-0 ${
                          g.estado === "Finalizado" || g.estado === "Cerrado"
                            ? "bg-green-50 text-green-700 border border-green-200"
                            : "bg-gray-100 text-gray-600 border border-gray-200"
                        }`}
                      >
                        {g.estado}
                      </span>
                    </div>

                    {/* Observations */}
                    <div className="text-sm text-gray-600 border-t border-gray-50 pt-2">
                      <span className="font-medium text-gray-700">Observaciones: </span>
                      {g.observaciones || "Sin observaciones"}
                    </div>

                    {/* Routines */}
                    {g.rutinasCompletadas?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                          Rutinas realizadas
                        </p>
                        <ul className="list-disc list-inside text-sm text-gray-600 space-y-0.5">
                          {g.rutinasCompletadas.map((r, idx) => (
                            <li key={idx}>{r}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Spare parts */}
                    {g.repuestosUsados?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                          Repuestos utilizados
                        </p>
                        <ul className="list-disc list-inside text-sm text-gray-600 space-y-0.5">
                          {g.repuestosUsados.map((r, idx) => (
                            <li key={idx}>
                              {r.articulo}{" "}
                              <span className="text-gray-400">— {r.cantidad_usada} unid.</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Footer */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border-t border-gray-50 pt-3">
                      <div className="text-xs text-gray-400 space-y-0.5">
                        <p>
                          <span className="font-medium text-gray-600">Realizado por:</span>{" "}
                          {g.realizadoPor?.name || "—"}
                        </p>
                        {g.personaAutoriza && (
                          <p>
                            <span className="font-medium text-gray-600">Autorizado por:</span>{" "}
                            {g.personaAutoriza}
                          </p>
                        )}
                      </div>

                      {g.estado === "Finalizado" && (
                        <button
                          onClick={() =>
                            descargarReporteIndividual(g.cronograma_id?._id)
                          }
                          className="inline-flex items-center gap-1.5 text-sm text-emerald-700 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 rounded-lg px-3 py-1.5 transition-colors"
                        >
                          <FileDown className="h-3.5 w-3.5" />
                          Imprimir
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* ── Predictivo IA ── */}
      <section className="space-y-4">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-50">
            <Brain className="h-4 w-4 text-violet-600" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-800">
              Histórico predictivo IA
            </h3>
            <p className="text-xs text-gray-400">
              {predictivos.length} predicción{predictivos.length !== 1 ? "es" : ""}
            </p>
          </div>
        </div>

        {predictivos.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-200 py-10 text-center">
            <Brain className="h-8 w-8 text-gray-200 mx-auto mb-2" />
            <p className="text-sm text-gray-400">Sin registros predictivos para este equipo</p>
          </div>
        ) : (
          <div className="space-y-3">
            {predictivos.map((p) => (
              <div
                key={p._id}
                className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm space-y-3"
              >
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-gray-800">
                    Riesgo{" "}
                    <span className="text-violet-700">{p.riesgo}</span>
                    {" · "}
                    Estado{" "}
                    <span className="text-gray-600">{p.estado}</span>
                  </p>
                  <p className="text-xs text-gray-400 shrink-0">
                    {new Date(p.createdAt).toLocaleDateString("es-CO")}
                  </p>
                </div>
                <p className="text-sm text-gray-600">{p.resumen || "Sin resumen"}</p>

                {p.gestiones?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                      Acciones registradas
                    </p>
                    <ul className="space-y-1">
                      {p.gestiones
                        .slice()
                        .reverse()
                        .map((g) => (
                          <li key={g._id} className="text-sm text-gray-600">
                            <span className="font-medium text-gray-700">[{g.estado}]</span>{" "}
                            {g.descripcion}{" "}
                            <span className="text-gray-400">
                              —{" "}
                              {new Date(g.fechaRegistro).toLocaleDateString("es-CO")}
                            </span>
                          </li>
                        ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default Historico;
